'use client';

import React, { memo, useCallback, useMemo } from 'react';
import { Check, ChevronRight } from 'lucide-react';
import posthog from 'posthog-js';

import { CommandItem } from '@repo/ui/components/ui/command';
import { cn } from '@repo/ui/lib/utils';

import { HOME_TAB } from '@/@types/enums';
import { getBrandCredibilityColor } from '@/lib/metric-utils';
import { BrandListItem } from '@/hooks/api/brand/model';
import { useAppTabs } from '@/providers/app-tabs-provider';

import { isStaticBrand, YOUTUBE_BRAND_ITEM } from '.';
import { AppNavigatorLogo } from './app-navigator-logo';

interface AppNavigatorListItemProps {
  type: 'fixed' | 'dynamic';
  brand: BrandListItem;
  label?: string;
  isSelected: boolean;
  onClick: () => void;
}

const PureAppNavigatorListItem: React.FC<AppNavigatorListItemProps> = ({
  type,
  brand,
  label,
  isSelected,
  onClick,
}) => {
  const { homeTab, setHomeTab } = useAppTabs();

  // Memoize credibility color calculation
  const percentColor = useMemo(
    () => getBrandCredibilityColor(brand?.metrics?.averageCredibility),
    [brand?.metrics?.averageCredibility],
  );

  // Memoize credibility percentage
  const credibilityPercent = useMemo(
    () => Math.round(brand.metrics?.averageCredibility ?? 0),
    [brand.metrics?.averageCredibility],
  );

  // Memoize static brand check
  const isStatic = useMemo(() => isStaticBrand(brand.id), [brand.id]);

  // Handle click with tab switching logic
  const handleClick = useCallback(() => {
    posthog.capture('select_brand', {
      location: 'app_navigator_list',
      // String(): SelectBrandSchema is z.string(); Brand.id is a number. Emitting
      // the raw number splits the cohort against mobile's String(brand.id) (PostHog
      // treats 123 and "123" as distinct). Fixed 2026-07-01 per the audit.
      brand_id: String(brand.id),
      brand_name: brand.brand,
      tab: homeTab,
    });
    onClick?.();
    // Only switch to NEWS tab if not selecting YouTube
    if (brand.id !== YOUTUBE_BRAND_ITEM.id && type !== 'dynamic') {
      setHomeTab(HOME_TAB.NEWS);
    }
  }, [brand.brand, brand.id, homeTab, onClick, setHomeTab, type]);

  return (
    <CommandItem
      onSelect={handleClick}
      className="group cursor-pointer gap-0 rounded-md text-black data-[selected=true]:bg-[#F1F5F9] data-[selected=true]:text-neutral-2 space-x-2 cred-md:space-x-3 cred-md:rounded-lg cred-lg:rounded-lg cred-xl:space-x-3.5 cred-xl:rounded-[10px] [&_svg]:size-4 cred-md:[&_svg]:size-5 cred-xl:[&_svg]:size-6"
    >
      {!isStatic && homeTab === HOME_TAB.NEWS ? (
        <div
          style={{ backgroundColor: percentColor }}
          className="flex rounded-[2px] px-1 py-0.5 cred-md:py-[3px] cred-xl:py-1 shadow-[inset_1px_2px_4px_rgba(62,62,62,0.25)] transition-opacity duration-300"
        >
          <span className="block w-8 cred-md:w-10 cred-xl:w-12 text-center font-satoshi text-xs cred-md:text-sm cred-lg:text-[15px] cred-xl:text-base font-bold leading-tight tracking-wide text-white">
            {credibilityPercent}%
          </span>
        </div>
      ) : (
        homeTab === HOME_TAB.NEWS && (
          <div className="px-1 py-0.5 cred-md:py-[3px] cred-xl:py-1">
            <div className="h-4 w-8 cred-md:w-10 cred-xl:w-12" />
          </div>
        )
      )}
      <AppNavigatorLogo
        brandId={brand.id}
        brand={brand.brand}
        logoCircleUrl={brand.logoCircleUrl}
      />
      <div className="flex flex-1 flex-col">
        <span className="text-sm font-medium leading-[14px] cred-md:text-base cred-md:leading-5 cred-lg:text-base cred-lg:leading-7 cred-xl:text-lg cred-xl:leading-[21px]">
          {label || brand.brand || '-'}
        </span>
      </div>
      <Check className={cn(isSelected ? 'opacity-100' : 'opacity-0')} />
      <div className="relative h-full w-1">
        <ChevronRight className="absolute right-0 top-1/2 -translate-y-1/2 opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
      </div>
    </CommandItem>
  );
};

export const AppNavigatorListItem = memo(
  PureAppNavigatorListItem,
  (prevProps, nextProps) => {
    return (
      prevProps.brand.id === nextProps.brand.id &&
      prevProps.label === nextProps.label &&
      prevProps.isSelected === nextProps.isSelected &&
      prevProps.brand.metrics?.averageCredibility ===
        nextProps.brand.metrics?.averageCredibility
    );
  },
);
